import React, { ReactElement, createContext, useContext, useEffect, useReducer } from 'react'
import { SettingsReducer, SettingsDispatcher } from '../reducers/SettingsReducer'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { generateDispatcher } from '../helpers/dispatch'
import { defaultContextConfig, initialiseState } from '../helpers/context'
import { AppContext } from '../contexts/AppContext'
import { isCurrentPage } from '../helpers/path'
import { redirect } from '../helpers/path'
import { ChildrenOnlyProps, ContextConfig } from '../helpers/context.d'
import { ensureUserSnippets, triggerInstanceChecks } from '../Settings/controller'
import { SettingsContextProvided, SettingsContextState } from './SettingsContext.d'

const defaultContextProvided: SettingsContextProvided = {
    state: {
        instances: {},
        prepare: {},
        sync: {}
    },
    dispatch: () => {
        throw Error('Dummy dispatch invoked')
    }
}

export const SettingsContext = createContext<SettingsContextProvided>(defaultContextProvided)

export const contextConfig: ContextConfig<SettingsContextState> = {
    ...defaultContextConfig,
    localStorageLocation: 'settings',
    defaultState: {
        instances: {},
        prepare: {}
    },
    retainedOnCacheClear: {
        instances: {},
        prepare: {}
    },
    freshState: {
        instances: {},
        prepare: {}
    }
}

const SettingsContextProvider = (props: ChildrenOnlyProps): ReactElement => {
    const appContext = useContext(AppContext)

    // Initialise.
    const [state, reducerDispatch] = useReducer(
        SettingsReducer,
        defaultContextProvided.state,
        initialiseState(contextConfig, appContext.appState))

    useEffect(() => localStorage.setItem('settings', JSON.stringify(state)),
        [state.snippets, state.snippetsPending, state.sync])

    const location = useLocation()
    const dispatch = generateDispatcher(reducerDispatch, SettingsDispatcher, appContext)

    // Track window focus for the status-check throttle, reconciling on mount: the listener fires only on
    // later visibility transitions, so an initial visit would otherwise find a stale (persisted) `false`.
    useEffect(() => {
        const syncFocus = (): void => dispatch({ type: 'SET_WINDOW_FOCUSED', focused: !document.hidden })
        syncFocus()
        document.addEventListener('visibilitychange', syncFocus)
        return () => document.removeEventListener('visibilitychange', syncFocus)
    }, [])

    ensureUserSnippets(state, dispatch)
    if (appContext.hasLoginScope('auth0')) {
        triggerInstanceChecks(state, dispatch,
            isCurrentPage('/admin', location.pathname) && appContext.hasLoginScope('admin'))
    }

    const navigate = useNavigate()
    const feature = useMatch('/redirect/:feature')?.params?.feature
    redirect(feature, dispatch, appContext?.appDispatch, location, navigate)

    return (
        <SettingsContext.Provider value={ { state, dispatch } }>
            <>{props.children}</>
        </SettingsContext.Provider>
    )
}

export default SettingsContextProvider
